import React from 'react'
import { Field } from 'redux-form'
import {
  makeStyles,
  styled,
  Input,
  Backdrop,
  Tooltip,
  Avatar
} from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'

import { colors, relationOptions } from '../../utils/constants'
import UserAvatar from '../shared/UserAvatar'
import CustomTable from '../table/CustomTable'
import RelationSelect from '../select/RelationshipSelect'
import StyledSVG from '../shared/StyledSVG'
import MaterialInput from '../shared/MaterialInput'
import { numberFromText } from '../../utils/helpers'

import triangleWarningSVG from '../../assets/icons/triangle-warning-red.svg'
import greenCheck from '../../assets/icons/rounded-green-check.svg'
import approvedSVG from '../../assets/icons/approved-authorization.svg'
import redFail from '../../assets/icons/rounded-red-fail.svg'
import Modal from '../modal/Modal'
import Button from '../button/Button'

const useStyles = makeStyles(theme => ({
  search_inputs: {
    marginTop: 24,
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    columnGap: 24,
    rowGap: 32
  },
  search_field: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
    height: 'fit-content',
    '& .MuiSelect-select.MuiSelect-select': {
      opacity: 0.5
    },
    '& p.label': {
      margin: 0,
      fontSize: 12,
      fontWeight: 600
    }
  },
  material_input_class: {
    width: '100%'
  },
  table_label: {
    margin: '36px 0px 16px 0px',
    display: 'flex',
    alignItems: 'center',
    columnGap: 8,
    '& p': {
      fontSize: 14,
      margin: 0
    },
    '& .temporary_indicative': {
      borderRadius: 56,
      width: 16,
      height: 16,
      backgroundColor: '#F8E7D6'
    }
  },
  modal_headers: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    rowGap: 16,
    padding: '32px 64px',
    '& .titles_wrapper': {
      display: 'flex',
      flexDirection: 'column',
      rowGap: 8
    },
    '& .title': {
      margin: 0,
      fontWeight: 600,
      color: '#EF7B8E',
      fontSize: 18
    },
    '& .subtitle': {
      margin: 0,
      fontWeight: 400,
      fontSize: 14
    }
  },
  modal_root: {
    height: 510,
    width: 630,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: 32
  },
  warning_icon_wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    backgroundColor: '#EF7C8F',
    borderRadius: 32,
    '& path': {
      stroke: '#FFFFFF'
    }
  },
  childrens_wrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  avatar_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    '& p': {
      fontWeight: 500,
      fontSize: 16,
      color: '#555555'
    }
  },
  actions_wrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 32,
    '& svg': {
      cursor: 'pointer'
    }
  },
  week_days_wrapper: {
    display: 'flex',
    columnGap: 4,
    flexWrap: 'wrap'
  },
  day_wrapper: {
    borderRadius: 8,
    backgroundColor: '#D9E8FA',
    width: 40,
    height: 32,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& p': {
      fontWeight: 500,
      fontSize: 14,
      margin: 0
    }
  },
  buttons_wrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 16
  },
  reject_input: {
    alignItems: 'flex-start',
    '& input, & textarea': {
      paddingTop: 12,
      paddingLeft: 15
    },
    width: 480,
    height: 140,
    borderRadius: 8,
    border: '1px solid #DADADA'
  },
  confirm_btn: {
    backgroundColor: '#386093',
    '&:hover': {
      backgroundColor: '#386093'
    }
  },
  cancel_btn: {
    backgroundColor: '#EF7C8F',
    '&:hover': {
      backgroundColor: '#EF7C8F'
    }
  },
  root_btn: {
    width: 165,
    color: '#FFFFFF',
    height: 48,
    padding: '14px 48px',
    gap: 10,
    borderRadius: 8,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '19.5px',
    textTransform: 'none !important'
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1
  },
  default_row_style: {
    backgroundColor: '#FFFFFF !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  temporary_row_style: {
    backgroundColor: '#F8E7D6 !important',
    '& td:last-child': {
      borderTopRightRadius: '16px',
      borderBottomRightRadius: '16px'
    },
    '& td:first-child': {
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px'
    }
  },
  queue_table: {
    '& .MuiTable-root': {
      borderCollapse: 'separate !important',
      borderSpacing: '0px 12px !important'
    }
  }
}))

const FormFieldWrapper = styled('div')(({ theme, gridColumn }) => ({
  gridColumn: `span ${gridColumn || 6}`
}))

const SearchPendingAuthorizationsForm = ({
  getPickupAuthorizations,
  authorizations,
  defaultParams,
  editPickupAuthorizationRSPA,
  approveTemporaryAuthorizationRSPA,
  alert
}) => {
  const [backDrop, setBackdrop] = React.useState({ open: false })
  const [confirmModal, setConfirmModal] = React.useState({
    open: false,
    id: null,
    reject_message: ''
  })
  const classes = useStyles()
  const firstPageAfterSearch = React.useRef(false)
  const columns = [
    {
      key: 'name',
      name: 'Reponsável',
      width: '25%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <UserAvatar
              avatarStyle={{
                width: 40,
                height: 40
              }}
              withoutUserName
              userName={row?.name}
              avatarUrl={row?.photo_url}
            />
            <p>{row?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'taxpayer_number',
      name: 'CPF',
      width: '12%',
      render: row => {
        return <p>{row?.taxpayer_number}</p>
      }
    },
    {
      key: 'phone',
      name: 'Telefone',
      width: '12%',
      render: row => {
        return <p>{row?.phone}</p>
      }
    },
    {
      key: 'vehicle_plate',
      name: 'Placa',
      width: '10%',
      render: row => {
        return <p>{row?.vehicle_plate?.toUpperCase()}</p>
      }
    },
    {
      key: 'childrens',
      name: 'Alunos',
      width: '25%',
      render: row => {
        return (
          <div className={classes.avatar_wrapper}>
            <AvatarGroup max={5} spacing={1}>
              {row?.pickup_authorization_children?.map((pckAuthChild, idx) => {
                const index =
                  numberFromText(pckAuthChild?.child?.name?.slice(0, 2)) %
                  colors.length
                return (
                  <Tooltip
                    key={`child[${idx}]`}
                    title={pckAuthChild?.child?.name}
                    arial-label='avatar-usuario'
                  >
                    <Avatar
                      style={{
                        height: '30px',
                        width: '30px',
                        backgroundColor:
                          // eslint-disable-next-line standard/computed-property-even-spacing
                          colors[index]
                      }}
                      src={pckAuthChild?.child?.avatar_url}
                    >
                      <p className={classes.without_photo_preview}>
                        {pckAuthChild?.child?.name[0]}
                      </p>
                    </Avatar>
                  </Tooltip>
                )
              })}
            </AvatarGroup>
            <p>{row?.pickup_authorization_children?.[0]?.child?.name}</p>
          </div>
        )
      }
    },
    {
      key: 'actions',
      name: 'Ações',
      width: '5%',
      render: row => {
        return (
          <div className={classes.actions_wrapper}>
            <StyledSVG
              onClick={() =>
                handleAuthorizationStatus('approved', row.id, row.pickup_type)
              }
              src={greenCheck}
              height={32}
              width={32}
            />
            <StyledSVG
              onClick={() => handleConfimModal(row.id)}
              src={redFail}
              height={32}
              width={32}
            />
          </div>
        )
      }
    }
  ]

  const handleRejectMessage = event => {
    setConfirmModal(prev => ({ ...prev, reject_message: event.target.value }))
  }
  const handleConfimModal = id => {
    setConfirmModal({ open: true, id, reject_message: '' })
  }
  const handleCloseModal = () => {
    setConfirmModal({ open: false, id: null, reject_message: '' })
  }
  const handleCloseBackdrop = () => {
    setBackdrop({ open: false })
    getPickupAuthorizations({
      page: 1,
      params: {
        ...defaultParams
      }
    })
  }

  const handleSearchFilteredAuthorization = event => {
    const fieldName = event?.target?.name
    const fieldValue = event?.target?.value
    getPickupAuthorizations({
      page: 1,
      params: {
        ...defaultParams,
        ...(fieldName === 'relationship_type'
          ? {
              [`q[${fieldName}_eq]`]: relationOptions.find(
                item => item.value === fieldValue
              ).enum
            }
          : {})
      }
    })
    firstPageAfterSearch.current = true
  }

  const handleAuthorizationStatus = async (status, id, pickup_type) => {
    const withFeedback = async (promise, reject) => {
      try {
        const res = await promise
        if (!reject) {
          setBackdrop({ open: true, approved: true })
        } else {
          handleCloseModal()
          alert('Autorização rejeitada!', 'success')
        }
        return res
      } catch (error) {
        alert('Erro ao processar autorização.', 'error')
        console.error(error)
      } finally {
        firstPageAfterSearch.current = true
      }
    }
    if (status === 'rejected') {
      await withFeedback(
        editPickupAuthorizationRSPA({
          id,
          data: {
            status: 'rejected',
            rejection_reason: confirmModal?.reject_message
          }
        }),
        true
      )
      getPickupAuthorizations({
        page: 1,
        params: {
          ...defaultParams
        }
      })
    } else {
      let promise
      if (pickup_type === 'temporary') {
        promise = approveTemporaryAuthorizationRSPA(id)
      } else {
        promise = editPickupAuthorizationRSPA({
          id,
          data: {
            status: 'approved'
          }
        })
      }
      await withFeedback(promise, false)
    }
  }

  const applyStyleByRowAttribute = row => {
    if (row?.pickup_type === 'temporary') {
      return classes.temporary_row_style
    }
    return classes.default_row_style
  }

  return (
    <div>
      <div className={classes.search_inputs}>
        <FormFieldWrapper gridColumn={6} className={classes.search_field}>
          <p className='label'>Busca</p>
          <Field
            rootClass={classes.material_input_class}
            component={MaterialInput}
            name='name'
            onKeyPress={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSearchFilteredAuthorization()
              }
            }}
            endBtnFunction={handleSearchFilteredAuthorization}
            placeholder='Buscar por nome do aluno ou resposável'
            type='text'
            autoComplete='off'
          />
        </FormFieldWrapper>
        <FormFieldWrapper gridColumn={4} className={`${classes.search_field}`}>
          <p className='label'>Tipo de responsável</p>
          <Field
            rootClass={classes.material_input_class}
            component={RelationSelect}
            onChange={event => handleSearchFilteredAuthorization(event)}
            name='relationship_type'
            placeholder='Selecione o tipo'
          />
        </FormFieldWrapper>
      </div>
      <div className={classes.table_label}>
        <div className='temporary_indicative' />
        <p>Responsáveis temporários</p>
      </div>
      <CustomTable
        columns={columns}
        from={'authorizations'}
        data={authorizations.items}
        fetchItems={getPickupAuthorizations}
        params={defaultParams}
        isFetching={authorizations.isFetching}
        pagination={authorizations.pagination}
        classRoot={classes.queue_table}
        applyStyleByRowAttribute={applyStyleByRowAttribute}
        columnsPointer={[0]}
        border={'none'}
        headCellRadius
        headColor={'rgba(173, 184, 204, 0.3)'}
        firstPageAfterSearch={firstPageAfterSearch}
      />
      <Modal
        open={confirmModal.open}
        handleClose={handleCloseModal}
        disablePadding
      >
        <div className={classes.modal_root}>
          <div className={classes.modal_headers}>
            <div className={classes.warning_icon_wrapper}>
              <StyledSVG src={triangleWarningSVG} height={32} width={32} />
            </div>
            <div className='titles_wrapper'>
              <h1 className='title'>Justificar recusa da autorização</h1>
              <p className='subtitle'>
                Informe o motivo pelo qual esta autorização está sendo recusada.
                Essa informação será enviada ao responsável.
              </p>
            </div>
          </div>
          <Input
            disableUnderline
            multiline
            classes={{ root: classes.reject_input }}
            placeholder='Descreva brevemente o motivo da recusa'
            onChange={handleRejectMessage}
            value={confirmModal.reject_message}
          />
          <div className={classes.buttons_wrapper}>
            <Button
              className={`${classes.root_btn} ${classes.cancel_btn}`}
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>
            <Button
              className={`${classes.root_btn} ${classes.confirm_btn}`}
              disabled={confirmModal.reject_message.length === 0}
              onClick={() =>
                handleAuthorizationStatus('rejected', confirmModal.id)
              }
            >
              Confirmar recusa
            </Button>
          </div>
        </div>
      </Modal>
      <Backdrop
        onClick={handleCloseBackdrop}
        className={classes.backdrop}
        open={backDrop.open}
      >
        <StyledSVG src={approvedSVG} height={200} width={300} />
      </Backdrop>
    </div>
  )
}

export default SearchPendingAuthorizationsForm
